(() => {
  'use strict';

  const productCache = new Map();
  let enhanceScheduled = false;

  const routesFor = () => ({
    add: (window.routes && window.routes.cart_add_url) || '/cart/add',
    change: (window.routes && window.routes.cart_change_url) || '/cart/change',
    update: (window.routes && window.routes.cart_update_url) || '/cart/update',
    cart: `${window.Shopify?.routes?.root || '/'}cart.js`
  });

  const jsonFetch = (url, options = {}) => fetch(url, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {})
    },
    ...options
  }).then(async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status === 422 || data.errors) {
      const error = new Error(data.description || data.message || 'Unable to update the cart.');
      error.payload = data;
      throw error;
    }
    return data;
  });

  const showMessage = (message, anchor) => {
    if (typeof window.showToast === 'function') {
      window.showToast(message, 3500, 'modal-error');
      return;
    }
    if (!anchor) return;
    let note = anchor.querySelector('.snug-cart-inline-message');
    if (!note) {
      note = document.createElement('div');
      note.className = 'snug-cart-inline-message';
      anchor.appendChild(note);
    }
    note.textContent = message;
    window.setTimeout(() => note.remove(), 3500);
  };

  const getCartElement = () => document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const getCartSections = () => {
    const cart = getCartElement();
    if (!cart || typeof cart.getSectionsToRender !== 'function') return [];
    return cart.getSectionsToRender();
  };

  const updateCount = (count) => {
    if (typeof count !== 'number') return;
    document.querySelectorAll('.cart-count').forEach((el) => {
      if (el.classList.contains('cart-count-drawer')) {
        el.textContent = String(count);
      } else {
        el.textContent = count > 100 ? '~' : String(count);
      }
    });
  };

  const renderCartSections = (state) => {
    const sections = getCartSections();
    const cart = getCartElement();
    let rendered = false;

    if (state && state.sections && sections.length) {
      sections.forEach((section) => {
        const htmlText = state.sections[section.id];
        const target = document.getElementById(section.id);
        if (!htmlText || !target) return;
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');
        const fresh = doc.querySelector('#minicart-form');
        if (fresh) {
          target.innerHTML = fresh.innerHTML;
          rendered = true;
        }
      });
    }

    if (typeof state?.item_count === 'number') updateCount(state.item_count);
    // Apply the final cart presentation before the drawer opens, so refreshed
    // base-theme markup is never painted as the previous cart design.
    enhanceAll();
    if (cart && typeof cart.cartAction === 'function') cart.cartAction();
    if (cart && typeof cart.open === 'function') cart.open();
    scheduleEnhance();
    return rendered;
  };

  const sectionPayload = () => {
    const sections = getCartSections();
    return {
      sections: sections.map((section) => section.id),
      sections_url: window.location.pathname
    };
  };

  const fetchProduct = (handle) => {
    if (!handle) return Promise.reject(new Error('Product is unavailable.'));
    if (!productCache.has(handle)) {
      productCache.set(handle, jsonFetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`));
    }
    return productCache.get(handle);
  };

  const optionNames = (product) => (product.options || []).map((option, index) => {
    if (typeof option === 'string') return option;
    return option.name || `Option ${index + 1}`;
  });

  const optionValues = (product, optionIndex) => {
    const configured = product.options && product.options[optionIndex];
    if (configured && typeof configured === 'object' && Array.isArray(configured.values)) {
      return configured.values.map((value) => typeof value === 'string' ? value : value.name || value.value).filter(Boolean);
    }
    return [...new Set((product.variants || []).map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
  };

  const prettyOptionName = (name) => /^color$/i.test(name) ? 'Colour' : name;

  const findVariant = (product, values) => (product.variants || []).find((variant) => {
    return (variant.options || []).every((value, index) => value === values[index]);
  });

  const makeSelect = (name, values, selected, className) => {
    const wrap = document.createElement('label');
    wrap.className = className;
    const select = document.createElement('select');
    select.setAttribute('aria-label', `Choose ${prettyOptionName(name)}`);
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      if (value === selected) option.selected = true;
      select.appendChild(option);
    });
    wrap.appendChild(select);
    return wrap;
  };

  const enhanceHeading = (wrapper) => {
    const heading = wrapper.querySelector('.minicart__header .minicart-heading');
    if (!heading) return;
    const countSource = heading.querySelector('.cart-count-drawer') || document.querySelector('.cart-count-drawer');
    const raw = countSource ? countSource.textContent : '';
    const count = Number((raw || '').replace(/[^0-9]/g, '')) || wrapper.querySelectorAll('.cart-item').length || 0;
    const signature = String(count);
    if (heading.dataset.snugHeading === signature && heading.querySelector('.snug-cart-heading-dot')) return;
    heading.dataset.snugHeading = signature;
    heading.innerHTML = `Cart <span class="snug-cart-heading-dot">•</span> <span class="cart-count cart-count-drawer snug-cart-heading-count">${count}</span> <span class="snug-cart-heading-word">${count === 1 ? 'item' : 'items'}</span>`;
  };

  const enhanceFreeDelivery = (wrapper) => {
    const hasItems = wrapper.querySelector('.cart-item');
    const existing = wrapper.querySelector('.snug-cart-free-delivery');
    if (!hasItems) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return;
    const header = wrapper.querySelector('.minicart__header');
    if (!header) return;
    const bar = document.createElement('div');
    bar.className = 'snug-cart-free-delivery';
    bar.innerHTML = `
      <div class="snug-cart-free-delivery__copy">
        <strong>FREE DELIVERY • AUSTRALIA-WIDE</strong>
        <small>No minimum spend</small>
      </div>
      <div class="snug-cart-free-delivery__track" aria-label="Free delivery unlocked">
        <span class="snug-cart-free-delivery__fill"></span>
        <span class="snug-cart-free-delivery__truck" aria-hidden="true">
          <svg width="18" height="14" viewBox="0 0 24 18" fill="none"><path d="M1 2h13v10H1V2Zm13 4h4.2l3 3.2V12H14V6ZM6 16a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm12 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>
        </span>
      </div>`;
    header.insertAdjacentElement('afterend', bar);
  };

  const enhanceCheckout = (wrapper) => {
    wrapper.querySelectorAll('.viewcart').forEach((node) => node.remove());
    wrapper.querySelectorAll('.snug-cart-au-note').forEach((node) => node.remove());

    const footer = wrapper.querySelector('.drawer-bottom');
    if (!footer) return;
    const checkout = footer.querySelector('.btn-checkout');
    if (checkout) {
      const label = checkout.querySelector('span') || checkout;
      if (label.textContent !== 'Check out') label.textContent = 'Check out';
    }

    const subtotal = footer.querySelector('.subtotal');
    if (subtotal) {
      const existingRows = [...footer.querySelectorAll('.snug-cart-discount-row')];
      existingRows.slice(1).forEach((row) => row.remove());

      const discountRow = existingRows[0] || null;
      if (discountRow && subtotal.nextElementSibling !== discountRow) {
        subtotal.insertAdjacentElement('afterend', discountRow);
      }

      // Totals and savings come from the same Shopify-rendered section.
      // No extra cart read or observer-driven price rewrite is needed.
    }

    if (checkout && !footer.querySelector('.snug-cart-payment-methods')) {
      const template = document.getElementById('snug-cart-payment-template');
      if (template && template.content) checkout.insertAdjacentElement('afterend', template.content.firstElementChild.cloneNode(true));
    }
  };

  const enhanceCartLine = async (item) => {
    if (item.dataset.snugVariantState === 'ready' || item.dataset.snugVariantState === 'loading') return;
    const nativeEdit = item.querySelector('minicart-item-edit');
    const handle = item.getAttribute('product-handle');
    if (!nativeEdit || !handle) return;
    item.dataset.snugVariantState = 'loading';

    try {
      const product = await fetchProduct(handle);
      if (!item.isConnected) return;
      const currentVariantId = String(nativeEdit.dataset.id || '');
      const currentVariant = (product.variants || []).find((variant) => String(variant.id) === currentVariantId);
      if (!currentVariant || optionNames(product).length === 0) {
        item.dataset.snugVariantState = 'skip';
        return;
      }

      const editor = document.createElement('div');
      editor.className = 'snug-line-variant-editor';
      editor.dataset.key = nativeEdit.dataset.key || '';
      editor.dataset.quantity = item.dataset.snugQuantity || nativeEdit.dataset.quantity || '1';
      editor.dataset.properties = item.dataset.snugProperties || '{}';
      editor.dataset.sellingPlan = item.dataset.snugSellingPlan || '';
      editor.dataset.handle = handle;
      editor.dataset.variantId = currentVariantId;

      optionNames(product).forEach((name, index) => {
        const field = makeSelect(name, optionValues(product, index), currentVariant.options[index], 'snug-line-variant-editor__field');
        field.querySelector('select').dataset.optionIndex = String(index);
        field.querySelector('select').dataset.previousValue = currentVariant.options[index];
        editor.appendChild(field);
      });

      const options = item.querySelector('.cart-options');
      if (options) {
        options.classList.add('snug-options-replaced');
        options.insertAdjacentElement('afterend', editor);
      } else {
        item.querySelector('.minicart__product-name')?.insertAdjacentElement('afterend', editor);
      }
      nativeEdit.classList.add('snug-native-edit-hidden');
      item.dataset.snugVariantState = 'ready';
    } catch (error) {
      item.dataset.snugVariantState = 'error';
    }
  };

  const restoreEditor = (editor) => {
    editor.querySelectorAll('select').forEach((select) => {
      if (select.dataset.previousValue != null) select.value = select.dataset.previousValue;
    });
  };

  const swapCartVariant = async (editor, newVariantId) => {
    const key = editor.dataset.key;
    if (!key) throw new Error('This cart item could not be updated.');

    const quantity = Math.max(1, Number(editor.dataset.quantity) || 1);
    let properties = {};
    try {
      properties = JSON.parse(editor.dataset.properties || '{}') || {};
    } catch (error) {
      properties = {};
    }

    const sellingPlan = Number(editor.dataset.sellingPlan || 0);
    const hasProperties = properties && Object.keys(properties).length > 0;
    const targetAlreadyExists = [...document.querySelectorAll('.cart-item minicart-item-edit[data-id]')]
      .some((node) => (
        String(node.dataset.id || '') === String(newVariantId)
        && String(node.dataset.key || '') !== String(key)
      ));

    const { add, change, update } = routesFor();

    /*
      Fast path: ordinary cart lines can be swapped in one Shopify update request.
      If the line carries properties/selling-plan metadata, or the target variant
      is already present, preserve the safer two-request path instead.
    */
    if (!hasProperties && !sellingPlan && !targetAlreadyExists) {
      const updates = {
        [key]: 0,
        [String(newVariantId)]: quantity
      };

      const finalState = await jsonFetch(`${update}.js`, {
        method: 'POST',
        body: JSON.stringify({
          updates,
          ...sectionPayload()
        })
      });

      renderCartSections(finalState);
      if (typeof finalState?.item_count === 'number') updateCount(finalState.item_count);
      return finalState;
    }

    const addItem = {
      id: Number(newVariantId),
      quantity
    };
    if (hasProperties) addItem.properties = properties;
    if (sellingPlan) addItem.selling_plan = sellingPlan;

    /* Safe metadata-preserving fallback — request 1 of 2. */
    await jsonFetch(`${add}.js`, {
      method: 'POST',
      body: JSON.stringify({ items: [addItem] })
    });

    // Adding a variant can change the old line key when a discount threshold
    // changes. The shared handler resolves the exact properties/selling plan.
    if (window.SnugCartMutations) {
      const finalState = await window.SnugCartMutations.change({ key, quantity: 0, target: editor });
      if (!finalState) throw new Error('Unable to finish changing this item. Please check your cart.');
      return finalState;
    }
    const finalState = await jsonFetch(`${change}.js`, {
      method: 'POST',
      body: JSON.stringify({
        id: key,
        quantity: 0,
        ...sectionPayload()
      })
    });

    renderCartSections(finalState);
    if (typeof finalState?.item_count === 'number') updateCount(finalState.item_count);
    return finalState;
  };

  const handleLineVariantChange = async (select) => {
    const editor = select.closest('.snug-line-variant-editor');
    const item = select.closest('.cart-item');
    if (!editor || !item) return;

    try {
      const product = await fetchProduct(editor.dataset.handle);
      const values = [...editor.querySelectorAll('select')].map((node) => node.value);
      const variant = findVariant(product, values);
      if (!variant || !variant.available) {
        restoreEditor(editor);
        showMessage('That colour and size combination is currently unavailable.', item.querySelector('.minicart__product-info'));
        return;
      }
      if (String(variant.id) === String(editor.dataset.variantId)) return;

      editor.classList.add('is-loading');
      editor.setAttribute('aria-busy', 'true');
      editor.querySelectorAll('select').forEach((node) => node.disabled = true);
      await swapCartVariant(editor, variant.id);
    } catch (error) {
      restoreEditor(editor);
      showMessage(error.message || 'Unable to change this option right now.', item.querySelector('.minicart__product-info'));
    } finally {
      if (editor.isConnected) {
        editor.classList.remove('is-loading');
        editor.removeAttribute('aria-busy');
        editor.querySelectorAll('select').forEach((node) => node.disabled = false);
      }
    }
  };

  const handleFromProductLink = (link) => {
    try {
      const url = new URL(link.href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const syncUpsellVariant = (card, product) => {
    const selects = [...card.querySelectorAll('.snug-cart-upsell-controls select')];
    const variant = findVariant(product, selects.map((select) => select.value));
    const button = card.querySelector('.snug-cart-upsell-add');
    card.dataset.snugSelectedVariant = variant && variant.available ? String(variant.id) : '';
    if (button) button.disabled = !(variant && variant.available);
  };

  const enhanceUpsellCard = async (card) => {
    if (card.dataset.snugUpsellState === 'ready' || card.dataset.snugUpsellState === 'loading') return;
    const link = card.querySelector('.product-item__name[href], a[href*="/products/"]');
    const handle = link ? handleFromProductLink(link) : '';
    if (!handle) return;
    card.dataset.snugUpsellState = 'loading';

    try {
      const product = await fetchProduct(handle);
      if (!card.isConnected) return;
      const variant = (product.variants || []).find((candidate) => candidate.available) || product.variants?.[0];
      if (!variant) {
        card.dataset.snugUpsellState = 'skip';
        return;
      }
      const controls = document.createElement('div');
      controls.className = 'snug-cart-upsell-controls';
      controls.dataset.handle = handle;
      const selects = document.createElement('div');
      selects.className = 'snug-cart-upsell-selects';

      optionNames(product).forEach((name, index) => {
        const field = makeSelect(name, optionValues(product, index), variant.options[index], 'snug-cart-upsell-select-wrap');
        field.querySelector('select').dataset.optionIndex = String(index);
        selects.appendChild(field);
      });

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'snug-cart-upsell-add';
      add.textContent = 'Add';
      controls.appendChild(selects);
      controls.appendChild(add);

      const info = card.querySelector('.product-item__information') || card;
      info.appendChild(controls);
      card.dataset.snugUpsellState = 'ready';
      card.dataset.snugSelectedVariant = String(variant.id);
      syncUpsellVariant(card, product);
    } catch (error) {
      card.dataset.snugUpsellState = 'error';
    }
  };

  const addUpsellVariant = async (button) => {
    const card = button.closest('.product-item');
    if (!card || !card.dataset.snugSelectedVariant) return;
    const variantId = Number(card.dataset.snugSelectedVariant);
    button.disabled = true;
    button.dataset.originalText = button.textContent;
    button.textContent = 'Adding…';
    try {
      const { add } = routesFor();
      const state = await jsonFetch(`${add}.js`, {
        method: 'POST',
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          ...sectionPayload()
        })
      });
      renderCartSections(state);
      if (typeof state.item_count === 'number') updateCount(state.item_count);
    } catch (error) {
      showMessage(error.message || 'Unable to add this item right now.', card);
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.textContent = button.dataset.originalText || 'Add';
      }
    }
  };

  const enhanceRecommendations = (wrapper) => {
    wrapper.querySelectorAll('.cart-recommend').forEach((recommendation) => {
      const card = recommendation.querySelector('.cart-upsell-item:not(:nth-child(n+2)) .product-item, .swiper-slide:not(:nth-child(n+2)) .product-item');
      if (card) enhanceUpsellCard(card);
    });
  };

  const enhanceAll = () => {
    document.querySelectorAll('.minicart__wrapper').forEach((wrapper) => {
      enhanceHeading(wrapper);
      enhanceFreeDelivery(wrapper);
      enhanceCheckout(wrapper);
      // Defer product JSON and recommendation controls until the cart opens.
      if (!wrapper.classList.contains('open')) return;
      wrapper.querySelectorAll('.cart-item[product-handle]').forEach((item) => enhanceCartLine(item));
      enhanceRecommendations(wrapper);
    });
  };

  function scheduleEnhance() {
    if (enhanceScheduled) return;
    enhanceScheduled = true;
    queueMicrotask(() => {
      enhanceScheduled = false;
      enhanceAll();
    });
  }

  document.addEventListener('change', (event) => {
    if (event.target.matches('.snug-line-variant-editor select')) {
      handleLineVariantChange(event.target);
      return;
    }
    if (event.target.matches('.snug-cart-upsell-controls select')) {
      const card = event.target.closest('.product-item');
      const controls = event.target.closest('.snug-cart-upsell-controls');
      if (!card || !controls) return;
      fetchProduct(controls.dataset.handle).then((product) => syncUpsellVariant(card, product)).catch(() => {});
    }
  });

  document.addEventListener('click', (event) => {
    const add = event.target.closest('.snug-cart-upsell-add');
    if (add) {
      event.preventDefault();
      addUpsellVariant(add);
    }
  });

  const start = () => {
    enhanceAll();
    const minicart = document.querySelector('.minicart');
    if (minicart) {
      new MutationObserver(scheduleEnhance).observe(minicart, { childList: true, subtree: true, characterData: true });
    }
    document.querySelectorAll('.minicart__wrapper').forEach((wrapper) => {
      new MutationObserver(scheduleEnhance).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
    });
    document.addEventListener('shopify:section:load', scheduleEnhance);
    window.addEventListener('glozinspeed-allScriptsLoaded', scheduleEnhance);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
